import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { API_PREFIX, resolveUrl } from 'src/core/constants/api.constants';
import { ListResponseDto } from 'src/core/models/request/list-response.dto';
import { NotificationDto } from 'src/core/models/notification/notification.dto';
import { NotificationListRequestDto } from 'src/core/models/notification/notification-list-request.dto';
import { SendTestNotificationRequest } from 'src/core/models/notification/send-test-notification-request.dto';

// This is the notification package's own conventional ABP route (`Sestek.Abp.Notification`), not an
// AgenticCX custom controller — deliberately NOT combined with `API_PREFIX` (see resolveUrl usage below).
const NOTIFICATION_BASE_URL = resolveUrl('/api/app/notification');

// This one IS an AgenticCX custom controller (`TestNotificationAppService`), so it goes through the
// regular `API_PREFIX` (`/api/agentic-cx`) convention like every other AgenticCX endpoint.
const TEST_NOTIFICATION_BASE_URL = resolveUrl(`${API_PREFIX}/test-notification`);

/**
 * Plain HttpClient wrapper for the notification REST API. `CrudService` was intentionally not used here:
 * it resolves its endpoint from a `type.apiUrl` static-property convention and doesn't have a clean way
 * to pass this endpoint's extra `status` query param, so a dedicated thin service is clearer than forcing
 * that convention.
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly http = inject(HttpClient);

  getList(request: NotificationListRequestDto): Observable<ListResponseDto<NotificationDto>> {
    let params = new HttpParams()
      .append('filters', JSON.stringify(request.filters))
      .append('sorters', JSON.stringify(request.sorters))
      .append('skipCount', JSON.stringify(request.skipCount))
      .append('maxResultCount', JSON.stringify(request.maxResultCount));

    if (request.status) {
      params = params.append('status', request.status);
    }

    return this.http.get<ListResponseDto<NotificationDto>>(NOTIFICATION_BASE_URL, { params });
  }

  getReadStatus(id: string): Observable<boolean> {
    return this.http.get<boolean>(`${NOTIFICATION_BASE_URL}/${id}/read-status`);
  }

  markAsRead(id: string): Observable<void> {
    return this.http.put<void>(`${NOTIFICATION_BASE_URL}/${id}/mark-as-read`, {});
  }

  markAsUnread(id: string): Observable<void> {
    return this.http.put<void>(`${NOTIFICATION_BASE_URL}/${id}/mark-as-unread`, {});
  }

  markAllAsRead(): Observable<void> {
    return this.http.post<void>(`${NOTIFICATION_BASE_URL}/mark-all-as-read`, {});
  }

  /** Sends a test notification (type-dependent shape) to the given username — used by the "Test" dialog. */
  sendTest(request: SendTestNotificationRequest): Observable<void> {
    return this.http.post<void>(`${TEST_NOTIFICATION_BASE_URL}/send`, request);
  }
}
